import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { createPortal } from 'react-dom';
import { Button, Spinner } from 'reactstrap';
import { RiDownload2Line } from 'react-icons/ri';
import { FaChevronDown } from 'react-icons/fa6';
import Heading from 'components/Heading';
import { authContext } from 'contexts/AuthContext';
import { ArrowLeft } from 'lucide-react';
import { FSBTable } from 'components/common/FSBTable';
import { showSequenceNumber } from 'utils/constants';

const USER_WISE_NOTIFICATION_STATS_QUERY = `
  query GetUserWiseStats($typeName: NotificationTypeName, $title: String!, $fromDate: String, $toDate: String, $pageNo: Int, $pageSize: Int) {
    getUserWiseNotificationStats(typeName: $typeName, title: $title, fromDate: $fromDate, toDate: $toDate, pageNo: $pageNo, pageSize: $pageSize) {
      totalCount
      totalPages
      content {
        traderId
        traderName
        traderMobile
        deliveredCount
        seenCount
        openedCount
        totalCount
      }
    }
  }
`;

const USER_NOTIFICATION_DETAILS_QUERY = `
  query GetUserNotificationDetails($traderId: ID!, $typeName: NotificationTypeName, $title: String!, $fromDate: String, $toDate: String, $pageNo: Int, $pageSize: Int) {
    getUserNotificationDetails(traderId: $traderId, typeName: $typeName, title: $title, fromDate: $fromDate, toDate: $toDate, pageNo: $pageNo, pageSize: $pageSize) {
      content {
        id
        title
        message
        typeName
        typeId
        osType
        image
        status
        isSeen
        isOpened
        createdAt
        createdAtIST
      }
      totalCount
      totalPages
    }
  }
`;

const TraderNameCell = ({ row, table }) => {
  const trader = row.original;
  const onSelectTrader = table.options.meta?.onSelectTrader;

  return (
    <button
      type="button"
      className="push-notification__trader-link"
      onClick={() => onSelectTrader?.(trader)}
    >
      {trader.traderName || '-'}
    </button>
  );
};

// Set this to false to restore the thumbnail-only behavior.
const ENABLE_NOTIFICATION_IMAGE_HOVER_PREVIEW = true;

const NotificationImagePreview = ({ src }) => {
  const [previewPosition, setPreviewPosition] = useState(null);

  if (!ENABLE_NOTIFICATION_IMAGE_HOVER_PREVIEW) {
    return <img src={src} alt="Notification" className="push-notification__detail-image" />;
  }

  const showPreview = (event) => {
    const thumbnail = event.currentTarget.getBoundingClientRect();
    const previewWidth = 160;
    const previewHeight = 200;
    const gap = 12;
    const fitsOnRight = thumbnail.right + gap + previewWidth <= window.innerWidth - 8;
    const maxTop = Math.max(8, window.innerHeight - previewHeight - 8);

    setPreviewPosition({
      left: fitsOnRight
        ? thumbnail.right + gap
        : Math.max(8, thumbnail.left - gap - previewWidth),
      top: Math.min(
        Math.max(8, thumbnail.top + (thumbnail.height - previewHeight) / 2),
        maxTop,
      ),
    });
  };

  return (
    <>
      <button
        type="button"
        className="push-notification__detail-image-trigger"
        aria-label="Preview notification image"
        onMouseEnter={showPreview}
        onMouseLeave={() => setPreviewPosition(null)}
        onFocus={showPreview}
        onBlur={() => setPreviewPosition(null)}
      >
        <img src={src} alt="" className="push-notification__detail-image" />
      </button>
      {previewPosition ? createPortal(
        <img
          src={src}
          alt=""
          aria-hidden="true"
          className="push-notification__detail-image-preview"
          style={previewPosition}
        />,
        document.body,
      ) : null}
    </>
  );
};

const USER_STATS_COLUMNS = [
  { accessorKey: 'SNo', header: 'S.NO.', enableSorting: false },
  { accessorKey: 'traderId', header: 'ID', enableSorting: false },
  { accessorKey: 'traderName', header: 'Name', enableSorting: false, cell: TraderNameCell },
  { accessorKey: 'traderMobile', header: 'Number', enableSorting: false },
  { accessorKey: 'deliveredCount', header: 'Delivered', enableSorting: false },
  { accessorKey: 'seenCount', header: 'Seen', enableSorting: false },
  { accessorKey: 'openedCount', header: 'Opened', enableSorting: false },
];

const USER_NOTIFICATION_DETAIL_COLUMNS = [
  { accessorKey: 'SNo', header: 'S.NO.', size: 55, minSize: 50, enableSorting: false },
  {
    accessorKey: 'title',
    header: 'TITLE',
    size: 170,
    minSize: 150,
    enableSorting: false,
    cell: (cell) => (
      <div className="push-notification__detail-text push-notification__detail-text--title" title={cell.getValue()}>
        {cell.getValue() || '-'}
      </div>
    ),
  },
  {
    accessorKey: 'message',
    header: 'MESSAGE',
    size: 300,
    minSize: 250,
    enableSorting: false,
    cell: (cell) => (
      <div className="push-notification__detail-text push-notification__detail-text--message" title={cell.getValue()}>
        {cell.getValue() || '-'}
      </div>
    ),
  },
  {
    accessorKey: 'image',
    header: 'IMAGE',
    size: 70,
    minSize: 60,
    enableSorting: false,
    cell: (cell) => (cell.getValue()
      ? <NotificationImagePreview src={cell.getValue()} />
      : '-'),
  },
  { accessorKey: 'status', header: 'STATUS', size: 100, minSize: 90, enableSorting: false },
  {
    accessorKey: 'isSeen',
    header: 'SEEN',
    size: 70,
    minSize: 60,
    enableSorting: false,
    cell: (cell) => (cell.getValue() ? 'Yes' : 'No'),
  },
  {
    accessorKey: 'isOpened',
    header: 'OPENED',
    size: 80,
    minSize: 70,
    enableSorting: false,
    cell: (cell) => (cell.getValue() ? 'Yes' : 'No'),
  },
  {
    accessorKey: 'createdAtIST',
    header: 'DATE & TIME (IST)',
    size: 180,
    minSize: 160,
    enableSorting: false,
    cell: (cell) => cell.getValue() || cell.row.original.createdAt || '-',
  },
];

const NOTIFICATION_STATS_QUERY = `
  query GetNotificationStats($typeName: NotificationTypeName, $title: String, $search: String, $date: String, $fromDate: String, $toDate: String, $pageNo: Int, $pageSize: Int) {
    getNotificationStats(typeName: $typeName, title: $title, search: $search, date: $date, fromDate: $fromDate, toDate: $toDate, pageNo: $pageNo, pageSize: $pageSize) {
      content {
        typeName
        title
        message
        delivered
        seen
        opened
        fromDate
        toDate
      }
      totalPages
    }
  }
`;

const toGraphQLString = (value) => (
  value === null || value === undefined ? 'null' : JSON.stringify(String(value))
);

const getNotificationExportQuery = (notification, fromDate, toDate, trader) => {
  const typeName = String(notification?.typeName || '').trim();
  if (!/^[_A-Za-z][_0-9A-Za-z]*$/.test(typeName)) {
    throw new Error('Notification type is not valid for export.');
  }

  const field = trader ? 'exportUserNotificationDetails' : 'exportUserWiseNotificationStats';
  const traderArgument = trader ? `traderId: ${toGraphQLString(trader.traderId)}, ` : '';

  return `query { ${field}(${traderArgument}typeName: ${typeName}, title: ${toGraphQLString(notification.title)}, fromDate: ${toGraphQLString(fromDate)}, toDate: ${toGraphQLString(toDate)}) }`;
};

const decodeCsvResponse = (base64Csv) => {
  if (typeof base64Csv !== 'string' || !base64Csv) {
    throw new Error('The export response did not contain CSV data.');
  }

  const bytes = Uint8Array.from(window.atob(base64Csv), (character) => character.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
};

const triggerCsvDownload = (csvText, fileName) => {
  const blob = new Blob(['\uFEFF', csvText], { type: 'text/csv;charset=utf-8' });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
};

const toFileNamePart = (value) => String(value || 'notification')
  .trim()
  .replace(/[^a-zA-Z0-9_-]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .toLowerCase() || 'notification';

const headerStyle = {
  padding: '12px 15px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#495057',
  background: '#f1f3f5',
  borderBottom: '2px solid #dee2e6',
  fontSize: '13px',
};

const cellStyle = {
  padding: '12px 15px',
  color: '#495057',
  verticalAlign: 'middle',
  fontSize: '13px',
};

const toSafeNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatNumber = (value) => toSafeNumber(value).toLocaleString('en-IN');

const PushNotification = ({ dateRange }) => {
  const { auth } = useContext(authContext);
  const [notificationStats, setNotificationStats] = useState([]);
  const [notificationTotalPages, setNotificationTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [selectedTrader, setSelectedTrader] = useState(null);
  const [userStats, setUserStats] = useState({ content: [], totalCount: 0, totalPages: 0 });
  const [userStatsLoading, setUserStatsLoading] = useState(false);
  const [userStatsError, setUserStatsError] = useState('');
  const [userNotificationDetails, setUserNotificationDetails] = useState({ content: [], totalCount: 0, totalPages: 0 });
  const [userNotificationDetailsLoading, setUserNotificationDetailsLoading] = useState(false);
  const [userNotificationDetailsError, setUserNotificationDetailsError] = useState('');
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [csvExportError, setCsvExportError] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [detailPageNo, setDetailPageNo] = useState(1);
  const [detailPageSize, setDetailPageSize] = useState(10);
  const [notificationPageNo, setNotificationPageNo] = useState(1);
  const [notificationPageSize, setNotificationPageSize] = useState(10);
  const [expandedMobileCardKey, setExpandedMobileCardKey] = useState(null);
  const [expandedMobileMessageKey, setExpandedMobileMessageKey] = useState(null);

  const downloadNotificationCsv = useCallback(async (notification, trader = null) => {
    if (csvDownloading) return;
    setCsvDownloading(true);
    setCsvExportError('');

    try {
      if (!auth?.data?.token) throw new Error('Authentication token not found.');

      const exportField = trader
        ? 'exportUserNotificationDetails'
        : 'exportUserWiseNotificationStats';
      const fromDate = dateRange?.startDate
        ? moment(dateRange.startDate).format('YYYY-MM-DD')
        : null;
      const toDate = dateRange?.endDate
        ? moment(dateRange.endDate).format('YYYY-MM-DD')
        : null;
      const response = await axios.post(
        process.env.REACT_APP_GRAPHQL_ENDPOINT,
        { query: getNotificationExportQuery(notification, fromDate, toDate, trader) },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.data.token}`,
          },
        },
      );

      if (response?.data?.errors?.length) {
        throw new Error(response.data.errors[0]?.message || 'Unable to export push notification data.');
      }

      const csvText = decodeCsvResponse(response?.data?.data?.[exportField]);
      const fileName = trader
        ? `notification-details-${toFileNamePart(trader.traderName)}-${toFileNamePart(trader.traderId)}`
        : `notification-stats-${toFileNamePart(notification.title)}`;
      triggerCsvDownload(csvText, fileName);
    } catch (error) {
      setCsvExportError(
        error?.response?.data?.message || error?.message || 'Unable to export push notification data.',
      );
    } finally {
      setCsvDownloading(false);
    }
  }, [auth?.data?.token, csvDownloading, dateRange?.startDate, dateRange?.endDate]);

  const fetchUserStats = useCallback(async (notification, requestedPage, requestedPageSize) => {
    if (!notification) return;
    setUserStatsLoading(true);
    setUserStatsError('');
    try {
      if (!auth?.data?.token) throw new Error('Authentication token not found.');
      const response = await axios.post(
        process.env.REACT_APP_GRAPHQL_ENDPOINT,
        {
          query: USER_WISE_NOTIFICATION_STATS_QUERY,
          variables: {
            typeName: notification.typeName,
            title: notification.title,
            fromDate: dateRange?.startDate ? moment(dateRange.startDate).format('YYYY-MM-DD') : null,
            toDate: dateRange?.endDate ? moment(dateRange.endDate).format('YYYY-MM-DD') : null,
            pageNo: requestedPage - 1,
            pageSize: requestedPageSize,
          },
        },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.data.token}` } },
      );
      if (response?.data?.errors?.length) throw new Error(response.data.errors[0]?.message || 'Unable to load notification recipients.');
      const result = response?.data?.data?.getUserWiseNotificationStats || {};
      setUserStats({ content: result.content || [], totalCount: result.totalCount || 0, totalPages: result.totalPages || 0 });
    } catch (error) {
      setUserStatsError(error?.response?.data?.message || error?.message || 'Unable to load notification recipients.');
      setUserStats({ content: [], totalCount: 0, totalPages: 0 });
    } finally {
      setUserStatsLoading(false);
    }
  }, [auth?.data?.token, dateRange?.startDate, dateRange?.endDate]);

  const fetchUserNotificationDetails = useCallback(async (notification, trader, requestedPage, requestedPageSize) => {
    if (!notification || !trader?.traderId) return;
    setUserNotificationDetailsLoading(true);
    setUserNotificationDetailsError('');
    try {
      if (!auth?.data?.token) throw new Error('Authentication token not found.');
      const response = await axios.post(
        process.env.REACT_APP_GRAPHQL_ENDPOINT,
        {
          query: USER_NOTIFICATION_DETAILS_QUERY,
          variables: {
            traderId: String(trader.traderId),
            typeName: notification.typeName,
            title: notification.title,
            fromDate: dateRange?.startDate ? moment(dateRange.startDate).format('YYYY-MM-DD') : null,
            toDate: dateRange?.endDate ? moment(dateRange.endDate).format('YYYY-MM-DD') : null,
            pageNo: requestedPage - 1,
            pageSize: requestedPageSize,
          },
        },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.data.token}` } },
      );
      if (response?.data?.errors?.length) {
        throw new Error(response.data.errors[0]?.message || 'Unable to load notification details.');
      }
      const result = response?.data?.data?.getUserNotificationDetails || {};
      setUserNotificationDetails({
        content: Array.isArray(result.content) ? result.content : [],
        totalCount: Number(result.totalCount) || 0,
        totalPages: Number(result.totalPages) || 0,
      });
    } catch (error) {
      setUserNotificationDetailsError(
        error?.response?.data?.message || error?.message || 'Unable to load notification details.',
      );
      setUserNotificationDetails({ content: [], totalCount: 0, totalPages: 0 });
    } finally {
      setUserNotificationDetailsLoading(false);
    }
  }, [auth?.data?.token, dateRange?.startDate, dateRange?.endDate]);

  useEffect(() => {
    if (selectedNotification) fetchUserStats(selectedNotification, pageNo, pageSize);
  }, [selectedNotification, pageNo, pageSize, dateRange?.startDate, dateRange?.endDate, fetchUserStats]);

  useEffect(() => {
    if (selectedNotification && selectedTrader) {
      fetchUserNotificationDetails(selectedNotification, selectedTrader, detailPageNo, detailPageSize);
    }
  }, [selectedNotification, selectedTrader, detailPageNo, detailPageSize, fetchUserNotificationDetails]);

  const handlePageChange = ({ pageNo: nextPage, pageSize: nextPageSize }) => {
    setPageSize(nextPageSize);
    setPageNo(nextPageSize === pageSize ? nextPage : 1);
  };

  const handleDetailPageChange = ({ pageNo: nextPage, pageSize: nextPageSize }) => {
    setDetailPageSize(nextPageSize);
    setDetailPageNo(nextPageSize === detailPageSize ? nextPage : 1);
  };

  const handleTraderSelect = (trader) => {
    setSelectedTrader(trader);
    setCsvExportError('');
    setDetailPageNo(1);
    setDetailPageSize(10);
    setUserNotificationDetailsError('');
    setUserNotificationDetails({ content: [], totalCount: 0, totalPages: 0 });
  };

  const userStatsColumns = useMemo(
    () => [
      {
        ...USER_STATS_COLUMNS[0],
        cell: (cell) => showSequenceNumber(cell, { pageNo, pageSize }),
      },
      ...USER_STATS_COLUMNS.slice(1),
    ],
    [pageNo, pageSize],
  );

  const userNotificationDetailColumns = useMemo(
    () => [
      {
        ...USER_NOTIFICATION_DETAIL_COLUMNS[0],
        cell: (cell) => showSequenceNumber(cell, { pageNo: detailPageNo, pageSize: detailPageSize }),
      },
      ...USER_NOTIFICATION_DETAIL_COLUMNS.slice(1),
    ],
    [detailPageNo, detailPageSize],
  );

  const fetchNotificationStats = useCallback(async (startDate, endDate, requestedPage, requestedPageSize) => {
    setLoading(true);
    setErrorMessage('');

    try {
      if (!auth?.data?.token) {
        throw new Error('Authentication token not found.');
      }

      const fromDate = startDate
        ? moment(startDate).format('YYYY-MM-DD')
        : null;
      const toDate = endDate
        ? moment(endDate).format('YYYY-MM-DD')
        : null;

      const response = await axios.post(
        process.env.REACT_APP_GRAPHQL_ENDPOINT,
        {
          query: NOTIFICATION_STATS_QUERY,
          variables: {
            typeName: null,
            title: null,
            date: null,
            fromDate,
            toDate,
            search: null,
            pageNo: requestedPage - 1,
            pageSize: requestedPageSize,
          },
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.data?.token}`,
          },
        },
      );

      if (response?.data?.errors?.length) {
        throw new Error(response.data.errors[0]?.message || 'Unable to load push notification data.');
      }

      const result = response?.data?.data?.getNotificationStats || {};
      setNotificationStats(Array.isArray(result.content) ? result.content : []);
      setNotificationTotalPages(Number(result.totalPages) || 0);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Unable to load push notification data.',
      );
      setNotificationStats([]);
      setNotificationTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [auth?.data?.token]);

  useEffect(() => {
    fetchNotificationStats(dateRange?.startDate || null, dateRange?.endDate || null, notificationPageNo, notificationPageSize);
  }, [fetchNotificationStats, dateRange?.startDate, dateRange?.endDate, notificationPageNo, notificationPageSize]);

  useEffect(() => {
    setNotificationPageNo(1);
  }, [dateRange?.startDate, dateRange?.endDate]);

  const handleNotificationPageChange = ({ pageNo: nextPage, pageSize: nextPageSize }) => {
    setNotificationPageSize(nextPageSize);
    setNotificationPageNo(nextPageSize === notificationPageSize ? nextPage : 1);
  };

  const normalizedStats = useMemo(
    () => notificationStats.map((item) => {
      const typeName = item?.typeName || '-';
      const title = item?.title || '-';
      const message = item?.message || '-';
      const delivered = toSafeNumber(item?.delivered);
      const seen = toSafeNumber(item?.seen);
      const opened = toSafeNumber(item?.opened);

      return {
        key: JSON.stringify([
          typeName,
          title,
          message,
          item?.fromDate || null,
          item?.toDate || null,
          delivered,
          seen,
          opened,
        ]),
        typeName,
        title,
        message,
        delivered,
        seen,
        opened,
      };
    }),
    [notificationStats],
  );

  let bodyContent = null;

  if (loading) {
    bodyContent = (
      <div className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading push notification analytics...</span>
          </div>
          <span className="ms-3 text-muted">Loading push notification analytics...</span>
        </div>
      </div>
    );
  } else if (normalizedStats.length === 0) {
    bodyContent = (
      <div className="daily-price-sharing__empty-state">
        No push notification stats available.
      </div>
    );
  } else {
    bodyContent = (
      <>
        <div className="analytics-mobile-list push-notification-mobile-list">
          {normalizedStats.map((item, index) => {
            const cardKey = `${item.key}-${index}`;
            const isExpanded = expandedMobileCardKey === cardKey;
            const isMessageExpanded = expandedMobileMessageKey === cardKey;

            return (
              <div className="analytics-mobile-card" key={cardKey}>
                <div className="analytics-mobile-card__summary">
                  <div className="analytics-mobile-card__identity">
                    <button
                      type="button"
                      className="analytics-mobile-card__name push-notification__title push-notification__title-button"
                      onClick={() => {
                        setSelectedNotification(item);
                        setPageNo(1);
                      }}
                    >
                      {item.title}
                    </button>
                    <div className="analytics-mobile-card__meta">
                      {`S.No. ${(notificationPageNo - 1) * notificationPageSize + index + 1}`}
                    </div>
                  </div>
                  <div className="analytics-mobile-card__aside">
                    <span className="analytics-mobile-card__pill muted">
                      {item.typeName}
                    </span>
                    <div className="analytics-mobile-card__tools">
                      <button
                        type="button"
                        className={`analytics-mobile-card__expand-toggle ${
                          isExpanded ? 'is-open' : ''
                        }`}
                        onClick={() => {
                          setExpandedMobileCardKey((previousKey) =>
                            previousKey === cardKey ? null : cardKey,
                          );
                        }}
                        aria-expanded={isExpanded}
                        aria-label={
                          isExpanded
                            ? 'Collapse notification details'
                            : 'Expand notification details'
                        }
                      >
                        <FaChevronDown />
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded ? (
                  <div className="analytics-mobile-card__detail-list">
                    <div className="analytics-mobile-card__detail-row">
                      <span className="analytics-mobile-card__detail-label">
                        Message
                      </span>
                      <button
                        type="button"
                        className={`analytics-mobile-card__detail-value analytics-mobile-card__message ${
                          isMessageExpanded ? 'is-expanded' : ''
                        }`}
                        onClick={() => {
                          setExpandedMobileMessageKey((previousKey) =>
                            previousKey === cardKey ? null : cardKey,
                          );
                        }}
                        aria-label={
                          isMessageExpanded
                            ? 'Collapse full notification message'
                            : 'Expand full notification message'
                        }
                      >
                        {item.message}
                      </button>
                    </div>
                    <div className="analytics-mobile-card__stats-grid">
                      <div>
                        <span>Delivered</span>
                        <strong>{formatNumber(item.delivered)}</strong>
                      </div>
                      <div>
                        <span>Seen</span>
                        <strong>{formatNumber(item.seen)}</strong>
                      </div>
                      <div>
                        <span>Opened</span>
                        <strong>{formatNumber(item.opened)}</strong>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="analytics-desktop-table" style={{ overflowX: 'auto' }}>
          <table className="push-notification__table" style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13px',
            tableLayout: 'fixed',
          }}>
            <thead>
              <tr>
                <th style={{ ...headerStyle, width: '60px' }} scope="col">
                  S.No.
                </th>
                <th style={{ ...headerStyle, width: '240px' }} scope="col">
                  Title/Template Name
                </th>
                <th style={{ ...headerStyle, width: 'auto' }} scope="col">
                  Message
                </th>
                <th style={{ ...headerStyle, width: '100px', textAlign: 'center' }} scope="col">
                  Delivered
                </th>
                <th style={{ ...headerStyle, width: '80px', textAlign: 'center' }} scope="col">
                  Seen
                </th>
                <th style={{ ...headerStyle, width: '80px', textAlign: 'center' }} scope="col">
                  Opened
                </th>
              </tr>
            </thead>
            <tbody>
              {normalizedStats.map((item, index) => (
                <tr
                  key={item.key}
                  style={{
                    borderBottom: '1px solid #e9ecef',
                    background: index % 2 === 0 ? '#fff' : '#fafafa',
                  }}
                >
                  <td style={cellStyle}>{(notificationPageNo - 1) * notificationPageSize + index + 1}</td>
                  <td style={cellStyle}>
                    <div
                      className="push-notification__title-cell"
                      title={`${item.title} (${item.typeName})`}
                    >
                      <button
                        type="button"
                        className="push-notification__title push-notification__title-button"
                        onClick={() => { setSelectedNotification(item); setPageNo(1); }}
                      >
                        {item.title}
                      </button>
                      <span className="push-notification__template-name">({item.typeName})</span>
                    </div>
                  </td>
                  <td style={cellStyle}>
                    <div className="push-notification__message" title={item.message}>{item.message}</div>
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    <span className="push-notification__metric push-notification__metric--delivered">{formatNumber(item.delivered)}</span>
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    <span className="push-notification__metric push-notification__metric--seen">{formatNumber(item.seen)}</span>
                  </td>
                  <td style={{ ...cellStyle, textAlign: 'center' }}>
                    <span className="push-notification__metric push-notification__metric--opened">{formatNumber(item.opened)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {notificationTotalPages > 0 ? (
          <div
            className="d-flex mt-3 align-items-center justify-content-end table-pagination-container"
            style={{ paddingRight: '55px' }}
          >
            <span className="text-active text-nowrap f-12">
              {notificationPageNo} of {notificationTotalPages}
            </span>
            <select
              className="page-select form-select table-select f-12 fw-normal mx-3 d-inline-block"
              value={notificationPageSize}
              onChange={(event) => handleNotificationPageChange({
                pageNo: notificationPageNo,
                pageSize: Number(event.target.value),
              })}
            >
              {[10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>{size} per page</option>
              ))}
            </select>
            <div className="pagination">
              <button type="button" onClick={() => handleNotificationPageChange({ pageNo: 1, pageSize: notificationPageSize })} disabled={notificationPageNo <= 1}>{'<<'}</button>
              <button type="button" onClick={() => handleNotificationPageChange({ pageNo: notificationPageNo - 1, pageSize: notificationPageSize })} disabled={notificationPageNo <= 1}>{'<'}</button>
              <button type="button" onClick={() => handleNotificationPageChange({ pageNo: notificationPageNo + 1, pageSize: notificationPageSize })} disabled={notificationPageNo >= notificationTotalPages}>{'>'}</button>
              <button type="button" onClick={() => handleNotificationPageChange({ pageNo: notificationTotalPages, pageSize: notificationPageSize })} disabled={notificationPageNo >= notificationTotalPages}>{'>>'}</button>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  if (selectedNotification && selectedTrader) {
    return (
      <div className="push-notification-container">
        <div className="daily-price-sharing__header push-notification__detail-header">
          <div className="push-notification__detail-header-content">
            <button
              type="button"
              className="push-notification__back"
              onClick={() => {
                setSelectedTrader(null);
                setCsvExportError('');
              }}
            >
              <ArrowLeft size={18} /> Back
            </button>
            <div className="push-notification__detail-header-title">
              <Heading title={selectedTrader.traderName} />
            </div>
            <div className="push-notification__download-actions">
              <Button
                className="gray-btn csv-btn push-notification__download-button"
                color="link"
                size="sm"
                type="button"
                aria-label="Download user notification details as CSV"
                title="Download CSV"
                onClick={() => downloadNotificationCsv(selectedNotification, selectedTrader)}
                disabled={csvDownloading}
              >
                {csvDownloading ? (
                  <Spinner color="primary" size="sm" className="small-loader" />
                ) : (
                  <RiDownload2Line size={16} className="fw-bold" />
                )}
              </Button>
            </div>
          </div>
        </div>
        {userNotificationDetailsError ? (
          <div className="daily-price-sharing__error" role="alert">
            {userNotificationDetailsError}
          </div>
        ) : null}
        {csvExportError ? (
          <div className="daily-price-sharing__error" role="alert">
            {csvExportError}
          </div>
        ) : null}
        <div className="analytics-mobile-list push-notification-user-mobile-list">
          {userNotificationDetailsLoading ? (
            <div className="analytics-mobile-empty">Loading...</div>
          ) : null}
          {!userNotificationDetailsLoading &&
          userNotificationDetails.content.length === 0 ? (
            <div className="analytics-mobile-empty">No notification details found.</div>
          ) : null}
          {!userNotificationDetailsLoading
            ? userNotificationDetails.content.map((item, index) => {
                const cardKey = `detail-${item.id || item.title}-${index}`;
                const isExpanded = expandedMobileCardKey === cardKey;
                const isMessageExpanded = expandedMobileMessageKey === cardKey;
                const serialNo = (detailPageNo - 1) * detailPageSize + index + 1;

                return (
                  <div className="analytics-mobile-card" key={cardKey}>
                    <div className="analytics-mobile-card__summary">
                      <div className="analytics-mobile-card__identity">
                        <div className="analytics-mobile-card__name push-notification__title">
                          {item.title || '-'}
                        </div>
                        <div className="analytics-mobile-card__meta">
                          {`S.No. ${serialNo} | ${item.createdAtIST || item.createdAt || '-'}`}
                        </div>
                      </div>
                      <div className="analytics-mobile-card__aside">
                        <span className="analytics-mobile-card__pill muted">
                          {item.typeName || item.status || 'TYPE'}
                        </span>
                        <div className="analytics-mobile-card__tools">
                          <button
                            type="button"
                            className={`analytics-mobile-card__expand-toggle ${
                              isExpanded ? 'is-open' : ''
                            }`}
                            onClick={() => {
                              setExpandedMobileCardKey((previousKey) =>
                                previousKey === cardKey ? null : cardKey,
                              );
                            }}
                            aria-expanded={isExpanded}
                            aria-label={
                              isExpanded
                                ? 'Collapse notification details'
                                : 'Expand notification details'
                            }
                          >
                            <FaChevronDown />
                          </button>
                        </div>
                      </div>
                    </div>

                    {isExpanded ? (
                      <div className="analytics-mobile-card__detail-list">
                        <div className="analytics-mobile-card__detail-row">
                          <span className="analytics-mobile-card__detail-label">
                            Message
                          </span>
                          <button
                            type="button"
                            className={`analytics-mobile-card__detail-value analytics-mobile-card__message ${
                              isMessageExpanded ? 'is-expanded' : ''
                            }`}
                            onClick={() => {
                              setExpandedMobileMessageKey((previousKey) =>
                                previousKey === cardKey ? null : cardKey,
                              );
                            }}
                            aria-label={
                              isMessageExpanded
                                ? 'Collapse full notification message'
                                : 'Expand full notification message'
                            }
                          >
                            {item.message || '-'}
                          </button>
                        </div>
                        {item.image ? (
                          <div className="analytics-mobile-card__detail-row">
                            <span className="analytics-mobile-card__detail-label">
                              Image
                            </span>
                            <span className="analytics-mobile-card__detail-value">
                              <NotificationImagePreview src={item.image} />
                            </span>
                          </div>
                        ) : null}
                        <div className="analytics-mobile-card__stats-grid">
                          <div>
                            <span>Status</span>
                            <strong>{item.status || '-'}</strong>
                          </div>
                          <div>
                            <span>Seen</span>
                            <strong>{item.isSeen ? 'Yes' : 'No'}</strong>
                          </div>
                          <div>
                            <span>Opened</span>
                            <strong>{item.isOpened ? 'Yes' : 'No'}</strong>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })
            : null}
        </div>
        <FSBTable
          data={userNotificationDetails.content}
          columns={userNotificationDetailColumns}
          totalCount={userNotificationDetails.totalCount}
          totalPages={userNotificationDetails.totalPages}
          currentPageNo={detailPageNo}
          pageSize={detailPageSize}
          keyField="id"
          showSearch={false}
          showFilters={false}
          showDelete={false}
          showPagination
          remote
          loading={userNotificationDetailsLoading}
          striped={false}
          hover={false}
          bordered={false}
          wrapperClass="table-responsive word-break push-notification-user-table"
          tableWraperClass="push-notification-user-table-border push-notification-detail-table-border"
          handlePageChange={handleDetailPageChange}
        />
      </div>
    );
  }

  if (selectedNotification) {
    return (
      <div className="push-notification-container">
        <div className="daily-price-sharing__header push-notification__detail-header">
          <div className="push-notification__detail-header-content">
            <button
              type="button"
              className="push-notification__back"
              onClick={() => {
                setSelectedNotification(null);
                setCsvExportError('');
              }}
            >
              <ArrowLeft size={18} /> Back
            </button>
            <div className="push-notification__detail-header-title">
              <Heading title={selectedNotification.title} />
            </div>
            <div className="push-notification__download-actions">
              <Button
                className="gray-btn csv-btn push-notification__download-button"
                color="link"
                size="sm"
                type="button"
                aria-label="Download user notification stats as CSV"
                title="Download CSV"
                onClick={() => downloadNotificationCsv(selectedNotification)}
                disabled={csvDownloading}
              >
                {csvDownloading ? (
                  <Spinner color="primary" size="sm" className="small-loader" />
                ) : (
                  <RiDownload2Line size={16} className="fw-bold" />
                )}
              </Button>
            </div>
          </div>
        </div>
        {userStatsError ? (
          <div className="daily-price-sharing__error" role="alert">
            {userStatsError}
          </div>
        ) : null}
        {csvExportError ? (
          <div className="daily-price-sharing__error" role="alert">
            {csvExportError}
          </div>
        ) : null}
        <div className="analytics-mobile-list push-notification-user-mobile-list">
          {userStatsLoading ? (
            <div className="analytics-mobile-empty">Loading...</div>
          ) : null}
          {!userStatsLoading && userStats.content.length === 0 ? (
            <div className="analytics-mobile-empty">No notification users found.</div>
          ) : null}
          {!userStatsLoading
            ? userStats.content.map((trader, index) => {
              const cardKey = `user-${trader.traderId}-${index}`;
              const isExpanded = expandedMobileCardKey === cardKey;
              const serialNo = (pageNo - 1) * pageSize + index + 1;

              return (
                <div className="analytics-mobile-card" key={cardKey}>
                  <div className="analytics-mobile-card__summary">
                    <div className="analytics-mobile-card__identity">
                      <button
                        type="button"
                        className="analytics-mobile-card__name push-notification__trader-link"
                        onClick={() => handleTraderSelect(trader)}
                      >
                        {trader.traderName || '-'}
                      </button>
                      <div className="analytics-mobile-card__meta">
                        {`S.No. ${serialNo}`}
                      </div>
                    </div>
                    <div className="analytics-mobile-card__aside">
                      <span className="analytics-mobile-card__pill muted">
                        {`ID ${trader.traderId || '-'}`}
                      </span>
                      <div className="analytics-mobile-card__tools">
                        <button
                          type="button"
                          className={`analytics-mobile-card__expand-toggle ${
                            isExpanded ? 'is-open' : ''
                          }`}
                          onClick={() => {
                            setExpandedMobileCardKey((previousKey) =>
                              previousKey === cardKey ? null : cardKey,
                            );
                          }}
                          aria-expanded={isExpanded}
                          aria-label={
                            isExpanded
                              ? 'Collapse trader stats'
                              : 'Expand trader stats'
                          }
                        >
                          <FaChevronDown />
                        </button>
                      </div>
                    </div>
                  </div>

                  {isExpanded ? (
                    <div className="analytics-mobile-card__detail-list">
                      <div className="analytics-mobile-card__detail-row">
                        <span className="analytics-mobile-card__detail-label">
                          Number
                        </span>
                        <span className="analytics-mobile-card__detail-value">
                          {trader.traderMobile || '-'}
                        </span>
                      </div>
                      <div className="analytics-mobile-card__stats-grid">
                        <div>
                          <span>Delivered</span>
                          <strong>{formatNumber(trader.deliveredCount)}</strong>
                        </div>
                        <div>
                          <span>Seen</span>
                          <strong>{formatNumber(trader.seenCount)}</strong>
                        </div>
                        <div>
                          <span>Opened</span>
                          <strong>{formatNumber(trader.openedCount)}</strong>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
              })
            : null}
        </div>
        <FSBTable
          data={userStats.content}
          columns={userStatsColumns}
          totalCount={userStats.totalCount}
          totalPages={userStats.totalPages}
          currentPageNo={pageNo}
          pageSize={pageSize}
          keyField="traderId"
          showSearch={false}
          showFilters={false}
          showDelete={false}
          showPagination
          remote
          meta={{ onSelectTrader: handleTraderSelect }}
          loading={userStatsLoading}
          striped={false}
          hover={false}
          bordered={false}
          wrapperClass="table-responsive word-break push-notification-user-table"
          tableWraperClass="push-notification-user-table-border push-notification-stats-table-border"
          handlePageChange={handlePageChange}
        />
      </div>
    );
  }

  return (
    <div
      className="whatsapp-analytics push-notification-analytics"
      style={{
        width: '100%',
        height: '100%',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: "'Work Sans', sans-serif",
      }}
    >
      {errorMessage ? (
        <div className="daily-price-sharing__error" style={{ margin: '15px' }} role="alert">
          {errorMessage}
        </div>
      ) : null}

      <div style={{ flex: 1, overflowY: 'auto', padding: '15px' }}>
        {bodyContent}
      </div>
    </div>
  );
};

export default PushNotification;
